import type { BpmnDefinitionsModel, BpmnTextAnnotation, RelationshipType } from './types';

/**
 * 出典の状態。docs/bpmn-dmn-ui.mdの「出典不明」「出典未記載」「参照先不明」を区別する。
 * unknownSource=relationshipがsourceUnknown、missingText=参照先の本文が空、danglingRef=参照先が無い。
 */
export type ProvenanceState = 'documented' | 'unknownSource' | 'missingText' | 'danglingRef';

export interface ProvenanceEntry {
  relationshipId: string;
  type: RelationshipType;
  state: ProvenanceState;
  annotationId: string;
  /** textAnnotationの本文。参照先が無い場合はnull。 */
  text: string | null;
}

export interface ProvenanceIndex {
  /** 索引の出所を示す定義XMLのdigest。別XMLの索引と取り違えないために持つ。 */
  readonly digest: string;
  entriesFor(elementId: string): ProvenanceEntry[];
  /** 出典から参照元要素への逆引き。 */
  elementsFor(annotationId: string): string[];
  /** 出典カタログ。どこからも参照されていない出典も含む。 */
  catalog(): Array<{ id: string; text: string; usedBy: string[] }>;
  /** 出典不明（sourceUnknown）の一覧。未記載・参照切れはmodel.warningsが持つ。 */
  unknowns(): Array<{ elementId: string; entry: ProvenanceEntry }>;
  /** process/lane/sequenceFlow/要素の表示名。 */
  labelFor(elementId: string): string;
}

// 参照先が無ければ本文の有無は判断できないため、danglingRefを最優先で判定する。
const stateOf = (
  type: RelationshipType,
  annotation: BpmnTextAnnotation | undefined,
): ProvenanceState => {
  if (!annotation) return 'danglingRef';
  if (type === 'sourceUnknown') return 'unknownSource';
  if (annotation.text === '') return 'missingText';
  return 'documented';
};

/**
 * 1つの定義XMLについての出典索引を作る。
 * 索引はmodelごとに閉じており、同名要素IDを持つ別XMLや旧runの索引と混ざらない。
 * 出典本文は原文のまま返す（テキストとして描画するのは利用側の責務。HTMLとして解釈しない）。
 */
export function buildProvenanceIndex(model: BpmnDefinitionsModel): ProvenanceIndex {
  const annotations = new Map<string, BpmnTextAnnotation>();
  const labels = new Map<string, string>();
  for (const process of model.processes) {
    labels.set(process.processKey, `プロセス「${process.name || process.processKey}」`);
    for (const [id, lane] of Object.entries(process.lanes)) {
      labels.set(id, `レーン「${lane.name || id}」`);
    }
    for (const flow of process.flows) labels.set(flow.id, `フロー ${flow.name || flow.id}`);
    for (const [id, element] of Object.entries(process.elements))
      labels.set(id, element.name || id);
    for (const [id, annotation] of Object.entries(process.textAnnotations)) {
      annotations.set(id, annotation);
    }
  }

  const entriesByElement = new Map<string, ProvenanceEntry[]>();
  const elementsByAnnotation = new Map<string, string[]>();
  for (const relationship of model.relationships) {
    for (const elementId of relationship.sources) {
      for (const annotationId of relationship.targets) {
        const annotation = annotations.get(annotationId);
        const entries = entriesByElement.get(elementId) ?? [];
        entries.push({
          relationshipId: relationship.id,
          type: relationship.type,
          state: stateOf(relationship.type, annotation),
          annotationId,
          text: annotation ? annotation.text : null,
        });
        entriesByElement.set(elementId, entries);
        const users = elementsByAnnotation.get(annotationId) ?? [];
        // 同じ要素が複数のrelationshipから同じ出典を指すことがあるので逆引きは重複を持たない。
        if (!users.includes(elementId)) users.push(elementId);
        elementsByAnnotation.set(annotationId, users);
      }
    }
  }

  return {
    digest: model.sourceDigest,
    entriesFor: (elementId) => entriesByElement.get(elementId) ?? [],
    elementsFor: (annotationId) => elementsByAnnotation.get(annotationId) ?? [],
    catalog: () =>
      [...annotations.values()].map((annotation) => ({
        id: annotation.id,
        text: annotation.text,
        usedBy: elementsByAnnotation.get(annotation.id) ?? [],
      })),
    unknowns: () =>
      [...entriesByElement].flatMap(([elementId, entries]) =>
        entries
          .filter((entry) => entry.state === 'unknownSource')
          .map((entry) => ({ elementId, entry })),
      ),
    labelFor: (elementId) => labels.get(elementId) ?? elementId,
  };
}
